import React, { type PropsWithChildren, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { I18nextProvider, useTranslation } from 'react-i18next';

import assistantI18n from './i18n';
import { notifyPromptOverridesChanged, subscribeToPromptOverrideChanges } from './promptOverrideSync';
import { CIQ_SHELL_EVENTS } from './shellEvents';
import type { ModelOption, ReasoningEffort } from './config/modelConfig';
import { DEFAULT_EFFORT, DEFAULT_MODEL } from './config/modelConfig';

import type { APIService, IContextDetection, UserService } from './interfaces/services';
import type { PageContext } from './services/ContextDetectionService';
import type {
  AgentAppAction,
  AgentChoiceOption,
  AgentContext,
  AgentMessage,
  AgentMode,
  AgentNames,
  AppActionContext,
  AppActionConfig,
  ApprovalDecision,
  AsyncMessageStatusResponse,
  AsyncMessageSubmitResponse,
  AttachmentMeta,
  GetConversationsResponse,
  Message,
  MessageButtonSpec,
  MessageContent,
  PendingApproval,
  PostMessageResponse,
  UserContextPayload,
} from './types';
import { EXIT_PLAN_MODE_TOOL_NAME, PLAN_MODE } from './types';
import { useConversationApi } from './useConversationApi';
import { useSessionStorageState } from './hooks/useSessionStorageState';
import { pollUntilDone } from './utils/polling';
import { useLatestRef } from 'quotient';
import { AIShellFeatures, MessageFeedback } from './types/api';

if (typeof crypto.randomUUID !== 'function') {
  crypto.randomUUID = () => {
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    bytes[6] = (bytes[6] % 16) + 0x40;
    bytes[8] = (bytes[8] % 64) + 0x80;
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  };
}

/** Pull an HTTP status off a thrown API error, whichever shape the host app's APIService uses. */
const getErrorStatus = (error: unknown): number | undefined => {
  if (!error || typeof error !== 'object') return undefined;
  return (error as any).response?.status ?? (error as any).status;
};

/**
 * Marks an error synthesized from a polled task result rather than raised by an HTTP call.
 *
 * The distinction matters for the sync fallback: a 404 from the submit request means the async
 * route is not deployed and retrying synchronously is correct, but a 404 *reported by the task*
 * means the work ran and failed on its own terms. Retrying that would submit the same decision
 * twice.
 */
const POLLED_ERROR_FLAG = '__polledTaskError';

const isPolledError = (error: unknown): boolean =>
  !!error && typeof error === 'object' && (error as any)[POLLED_ERROR_FLAG] === true;

export type AppActionHandler = (actionData: Record<string, any>, context: AppActionContext) => Promise<void>;
export type BulkAppActionHandler = (appActions: AgentAppAction[], context: AppActionContext) => Promise<void> | void;

/** The caller's live prompt-experiment override for one agent, if any — backs the chat banner. */
export type ActivePromptOverride = {
  uuid: string;
  agentName: string;
  note: string;
} | null;

type ActivePromptOverrideResponse = {
  uuid: string;
  agent_name: string;
  note: string;
} | null;

const ACTIVE_PROMPT_OVERRIDE_URL = '/api/assistant/prompt-experiments/active/';
const deactivatePromptOverrideUrl = (uuid: string) => `/api/assistant/prompt-experiments/${uuid}/deactivate/`;

const toActivePromptOverride = (response: ActivePromptOverrideResponse): ActivePromptOverride =>
  response ? { uuid: response.uuid, agentName: response.agent_name, note: response.note } : null;

/** An insight is opaque to the shared shell beyond having a stable id to key list rendering on. */
export type Insight = { id: string } & Record<string, unknown>;

/**
 * One button on an insight card. Pure data and a plain callback — never JSX — so a provider
 * registered from a different bundle (e.g. TQ, via the window-event bridge below) can describe a
 * card without handing over a React element for this shell's own React copy to render. A live
 * component crossing that boundary hits "invalid hook call": its hooks are bound to the other
 * bundle's React instance, not this one's.
 */
export type InsightCardAction = {
  key: string;
  label: string;
  loadingLabel?: string;
  doneLabel?: string;
  /** True if this action was already completed before the card was ever shown (e.g. on reload). */
  initiallyDone?: boolean;
  /** Once this action completes, every other action on the card becomes disabled. */
  locksCard?: boolean;
  onClick: () => Promise<void>;
};

/** Everything needed to render one insight card — see `InsightCardAction` for why this is data, not JSX. */
export type InsightCardContent = {
  headline: string;
  body: string;
  /** Optional small label shown above the headline (e.g. which context this insight applies to). */
  badge?: string;
  /** Optional content blocks (e.g. a chart) — reuses the same blocks the chat renders. */
  content?: MessageContent[];
  actions: InsightCardAction[];
};

/**
 * A product area's plug-in into the shared Insights tab. The shell never knows what an "insight"
 * looks like for a given area — it only asks `matches` which provider owns the current context,
 * then defers everything else (fetching, describing, generating) to that provider.
 */
export type InsightsProvider = {
  matches: (context: PageContext) => boolean;
  fetchInsights: () => Promise<Insight[]>;
  describeCard: (
    insight: Insight,
    helpers: {
      openChatAndSend: (message: string, attachments?: AttachmentMeta[]) => Promise<void>;
      /**
       * `openChatAndSend` alone throws "Trying to start a conversation without agent selected!"
       * unless an agent is already selected — call this first to cold-start a conversation with
       * a specific agent (e.g. from a card action with no prior chat context).
       */
      openAgentShell: (agent: AgentNames, context?: AgentContext) => void;
    },
  ) => InsightCardContent;
  generateInsights: () => Promise<void>;
  /**
   * Optional: resolves true if a generation run for the current context is already in flight
   * (e.g. the tab was closed/refreshed mid-run). When present, the tab uses it on mount to resume
   * showing the generating state instead of appearing idle while a run is still going.
   */
  hasActiveGeneration?: () => Promise<boolean>;
};

export type AIShellContextType = {
  isOpen: boolean;
  toggleShell: () => void;
  openShell: () => void;
  isExpanded: boolean;
  toggleExpanded: () => void;
  chatView: ChatViewEnum;
  tabIndex: TabsEnum;
  setTabIndex: (ti: TabsEnum) => void;
  agentThinking: boolean;
  agentStatusMessage: string | null;
  selectedAgent: AgentNames | string | null;
  setSelectedAgent: (name: AgentNames | string | null) => void;
  getConversations: (limit?: number, offset?: number) => Promise<GetConversationsResponse>;
  messageSendError: string | null;
  openAgentShell: (agent: AgentNames, context?: AgentContext) => void;
  openIntroFor: (agent: AgentNames | string) => void;
  backToAgentBrowser: () => void;
  /** Navigates to the full "All Chats" list view, replacing the tab bar. */
  openAllChats: () => void;
  /** Returns from the "All Chats" view to the in-progress chat, unchanged. */
  backToChat: () => void;
  currentConversation: CurrentConversation | null;
  openNewChat: () => void;
  send: (message: string, options?: { context?: AgentContext; attachments?: AttachmentMeta[] }) => Promise<void>;
  openChatAndSend: (message: string, attachments?: AttachmentMeta[]) => Promise<void>;
  ensureConversation: () => Promise<string>;
  createAttachment: (
    file: File,
    conversationId: string,
    onProgress: (pct: number) => void,
    signal: AbortSignal,
  ) => Promise<AttachmentMeta>;
  openConversation: (id: CurrentConversation['id']) => Promise<void>;
  abortPendingConversationMessage: () => void;
  /** Appends a local agent message bubble (e.g. after an app_action applies UI state). */
  appendAgentChatMessage: (contents: string) => void;
  // Context detection functionality
  pageDetectedContext: PageContext;
  updateContext: (context: Record<string, string | undefined>) => void;
  refreshContext: () => void;
  // App action handler registry
  registerAppActionHandler: (arg: { key: string; action: string; handler: AppActionHandler }) => void;
  unregisterAppActionHandler: (key: string) => void;
  registerBulkAppActionHandler: (arg: { key: string; handler: BulkAppActionHandler }) => void;
  unregisterBulkAppActionHandler: (key: string) => void;
  // Insights provider registry — first match wins, unlike the app-action registry's fan-out
  registerInsightsProvider: (key: string, provider: InsightsProvider) => void;
  unregisterInsightsProvider: (key: string) => void;
  getMatchingInsightsProvider: (context: PageContext) => InsightsProvider | undefined;
  // Shared insights cache — fetched once here so the header badge and the tab body never double-fetch
  insights: Insight[];
  insightsStatus: 'idle' | 'loading' | 'error';
  refreshInsights: () => Promise<void>;
  // App action for agent messages (labels + optional inline buttons)
  registerMessageAppActions: (messageId: string, actions: AppActionConfig[]) => void;
  getMessageAppActions: (messageId: string) => AppActionConfig[];
  // Panel-level buttons (rendered at top of chat, above messages)
  registerPanelButtons: (buttons: MessageButtonSpec[]) => void;
  getPanelButtons: () => MessageButtonSpec[];
  // Multiple-choice options from the latest agent response
  choices: AgentChoiceOption[];
  /** Write action the agent has queued and is waiting on a human decision for, if any. */
  pendingApproval: PendingApproval | null;
  /**
   * Approve or decline a queued write action. No-ops unless approvalId matches the currently
   * pending approval, so a stale control cannot resolve a newer action.
   */
  resolveApproval: (approvalId: string, decision: ApprovalDecision) => Promise<void>;
  // Feature toggles passed from the host app
  features: AIShellFeatures;
  // Debug mode for internal users
  debugMode: boolean;
  toggleDebugMode: () => void;
  // Model selection for dev model switching
  selectedModel: ModelOption;
  setSelectedModel: (model: ModelOption) => void;
  selectedEffort: ReasoningEffort;
  setSelectedEffort: (effort: ReasoningEffort) => void;
  /** Plan mode: the agent proposes a plan for approval before changing anything. */
  planMode: boolean;
  setPlanMode: (planMode: boolean) => void;
  // Message feedback (thumbs up/down + optional text)
  feedbackEnabled: boolean;
  messagesFeedbackData: Record<string, MessageFeedback> | undefined;
  handleFeedback: (messageId: string, feedback: number) => void;
  handleFeedbackResponse: (messageId: string, text: string) => Promise<void>;
  // Id of the agent message that should play the typewriter reveal animation, if any
  messageIdToReveal: string | undefined;
  handleMessageRevealed: () => void;
  /** The caller's live prompt-experiment override for the currently selected agent, if any.
   * Scoped per agent — unlike a global "one override for the user" flag, switching agents can
   * surface a different override (or none). Resolves to null for non-internal callers. */
  activePromptOverride: ActivePromptOverride;
  deactivateActivePromptOverride: () => Promise<void>;
};

export const AIShellContext = React.createContext<AIShellContextType | null>(null);

export const useAIShellContext = () => {
  const context = useContext(AIShellContext);

  if (context === null) {
    throw Error('Trying to use AIShellContext outside of AIShellContextProvider!');
  }

  return context;
};

/**
 * Safe version of useAIShellContext that returns null when outside provider.
 * Use this when the component may or may not be inside an AIShellContextProvider.
 */
export const useAIShellContextSafe = () => {
  return useContext(AIShellContext);
};

const createUserMessage = (contents: string, id?: string, attachments?: AttachmentMeta[]): Message => ({
  id: id ?? crypto.randomUUID(),
  type: 'user',
  contents,
  ...(attachments?.length ? { attachments } : {}),
});
const createCustomAgentMessage = (contents: string): Message => ({
  id: crypto.randomUUID(),
  type: 'agent',
  contents,
});
const createAgentMessage = (agentMessage: AgentMessage): Message => ({
  id: `${agentMessage.id}:agent`,
  type: 'agent',
  contents: agentMessage.response_message,
  ...(agentMessage.cited_sources?.length ? { cited_sources: agentMessage.cited_sources } : {}),
  ...(agentMessage.text_references && Object.keys(agentMessage.text_references).length
    ? { text_references: agentMessage.text_references }
    : {}),
  ...(Array.isArray(agentMessage.content) && agentMessage.content.length ? { content: agentMessage.content } : {}),
  ...(agentMessage.debug_messages?.length ? { debug_messages: agentMessage.debug_messages } : {}),
  ...(agentMessage.choices?.length ? { choices: agentMessage.choices } : {}),
  ...(agentMessage.pending_approval ? { pending_approval: agentMessage.pending_approval } : {}),
});
const parseAgentMessage = (agentMessage: AgentMessage): [Message, Message] => {
  return [createUserMessage(agentMessage.input_message, `${agentMessage.id}:user`), createAgentMessage(agentMessage)];
};

/** Keep only string/number values from detected PageContext, in the shape the backend expects. */
const filterDetectedContext = (context: PageContext): AgentContext => {
  const filtered: AgentContext = {};
  Object.entries(context).forEach(([key, value]) => {
    if (value && (typeof value === 'string' || typeof value === 'number')) {
      filtered[key] = value;
    }
  });
  return filtered;
};

const MAX_RECENTLY_VISITED_WORKSHEETS = 20;

export enum TabsEnum {
  CHAT,
  HISTORY,
  INSIGHTS,
}

export enum ChatViewEnum {
  BROWSER,
  INTRO,
  CHAT,
}

type CurrentConversation = {
  id: string;
  messages: Array<Message>;
};

export type UserSessionContext = {
  recentWorksheetIds?: string[];
};

type AIShellContextProviderProps = PropsWithChildren<{
  userService: UserService;
  apiService: APIService;
  /**
   * Context detection adapter for URL-based context extraction.
   * Required for context-specific agents to be filtered correctly.
   * If not provided, no context detection occurs (only global agents shown).
   */
  contextDetection?: IContextDetection;
  features?: AIShellFeatures;
}>;

const useDefaultContext = (_userService: UserService): AgentContext => {
  return useMemo(
    () => ({}), // Empty context - backend gets user from request
    [],
  );
};

const DEFAULT_FEATURES: AIShellFeatures = {};

const DEFAULT_CHOICES: Message['choices'] = [];

const ACTIVE_CONVERSATION_STORAGE_KEY = 'ciq-assistant-active-conversation-id';

export const AIShellContextProvider = ({
  children,
  userService,
  apiService,
  contextDetection,
  features = DEFAULT_FEATURES,
}: AIShellContextProviderProps) => {
  const { t } = useTranslation('assistant');
  const [storedConversationId, setStoredConversationId] = useSessionStorageState<string | null>({
    key: ACTIVE_CONVERSATION_STORAGE_KEY,
  });
  const [initialConversationId] = useState(() => storedConversationId);
  const [isOpen, setIsOpen] = useState<AIShellContextType['isOpen']>(() => !!initialConversationId);
  const [isExpanded, setIsExpanded] = useState<AIShellContextType['isExpanded']>(false);
  const toggleShell: AIShellContextType['toggleShell'] = useCallback(() => {
    setIsOpen((prev) => {
      const next = !prev;
      // Reset expansion when closing so re-opening returns to the default collapsed view.
      if (!next) {
        setIsExpanded(false);
      }
      return next;
    });
  }, []);
  const openShell: AIShellContextType['openShell'] = useCallback(() => {
    setIsOpen(true);
  }, []);
  const toggleExpanded: AIShellContextType['toggleExpanded'] = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  const {
    createConversation,
    sendMessage,
    sendMessageAsync,
    getMessageStatus,
    submitApprovalDecision,
    submitApprovalDecisionAsync,
    getConversations,
    getSingleConversation,
    submitFeedback,
    createAttachment,
  } = useConversationApi(apiService);

  const asyncPollControllerRef = useRef<AbortController | null>(null);
  const abortPendingConversationMessage: AIShellContextType['abortPendingConversationMessage'] = useCallback(() => {
    asyncPollControllerRef.current?.abort();
    asyncPollControllerRef.current = null;
  }, []);

  const [tabIndex, setTabIndex] = useState<TabsEnum>(TabsEnum.CHAT);
  const [chatView, setChatView] = useState<ChatViewEnum>(() =>
    initialConversationId ? ChatViewEnum.CHAT : ChatViewEnum.BROWSER,
  );

  const [messagesFeedbackData, setMessagesFeedbackData] =
    useState<AIShellContextType['messagesFeedbackData']>(undefined);

  const [selectedAgent, setSelectedAgentState] = useState<AIShellContextType['selectedAgent']>(null);
  const selectedAgentRef = useRef<AIShellContextType['selectedAgent']>(null);
  const setSelectedAgent = useCallback((agent: AIShellContextType['selectedAgent']) => {
    selectedAgentRef.current = agent;
    setSelectedAgentState(agent);
  }, []);

  const [activePromptOverride, setActivePromptOverride] = useState<ActivePromptOverride>(null);

  const promptExperimentsEnabled = features.isCIQInternalUser === true;
  const activeOverrideRequestRef = useRef(0);

  const refreshActivePromptOverride = useCallback(() => {
    if (!promptExperimentsEnabled || !selectedAgent) {
      setActivePromptOverride(null);
      return;
    }
    // Monotonic rather than a per-effect cancelled flag: agent switches and focus refetches can
    // be in flight together, and only the newest response may win.
    const requestId = activeOverrideRequestRef.current + 1;
    activeOverrideRequestRef.current = requestId;
    apiService
      .get<ActivePromptOverrideResponse>(ACTIVE_PROMPT_OVERRIDE_URL, { agent_name: selectedAgent })
      .then((response) => {
        if (requestId === activeOverrideRequestRef.current) setActivePromptOverride(toActivePromptOverride(response));
      })
      .catch(() => {
        if (requestId === activeOverrideRequestRef.current) setActivePromptOverride(null);
      });
  }, [apiService, selectedAgent, promptExperimentsEnabled]);

  useEffect(() => {
    refreshActivePromptOverride();
  }, [refreshActivePromptOverride]);

  useEffect(() => subscribeToPromptOverrideChanges(refreshActivePromptOverride), [refreshActivePromptOverride]);

  // Covers what the broadcast cannot: an edit made before this tab was listening, or from another
  // browser session entirely.
  useEffect(() => {
    window.addEventListener('focus', refreshActivePromptOverride);
    return () => window.removeEventListener('focus', refreshActivePromptOverride);
  }, [refreshActivePromptOverride]);

  const deactivateActivePromptOverride = useCallback(async () => {
    if (!activePromptOverride) return;
    await apiService.post<ActivePromptOverrideResponse>(deactivatePromptOverrideUrl(activePromptOverride.uuid), {});
    setActivePromptOverride(null);
    notifyPromptOverridesChanged();
  }, [apiService, activePromptOverride]);

  const [backgroundContext, setBackgroundContext] = useState<AgentContext>({});
  const [currentConversation, setCurrentConversation] = useState<AIShellContextType['currentConversation']>(null);
  const lastMessage = useMemo(() => {
    const messages = currentConversation?.messages ?? [];
    return messages[messages.length - 1];
  }, [currentConversation]);
  const choices = useMemo<AgentChoiceOption[]>(() => lastMessage?.choices ?? DEFAULT_CHOICES, [lastMessage]);
  const pendingApproval = useMemo<PendingApproval | null>(() => lastMessage?.pending_approval ?? null, [lastMessage]);
  const [messageSendError, setMessageSendError] = useState<string | null>(null);
  const defaultContext = useDefaultContext(userService);
  const [agentThinking, setAgentThinking] = useState<AIShellContextType['agentThinking']>(false);
  const [agentStatusMessage, setAgentStatusMessage] = useState<AIShellContextType['agentStatusMessage']>(null);
  const [messageIdToReveal, setMessageIdToReveal] = useState<string | undefined>(undefined);
  const [selectedModel, setSelectedModel] = useState<ModelOption>(DEFAULT_MODEL);
  const [selectedEffort, setSelectedEffort] = useState<ReasoningEffort>(DEFAULT_EFFORT);
  const [planMode, setPlanMode] = useState(false);
  const [debugMode, setDebugMode] = useState(false);
  const toggleDebugMode = useCallback(() => setDebugMode((prev) => !prev), []);

  const [pageDetectedContext, setPageDetectedContext] = useState<PageContext>(
    () => contextDetection?.detectContext() ?? {},
  );
  const customContextRef = useRef<Record<string, string>>({});
  const userSessionContextRef = useRef<UserSessionContext>({});
  const updateUserSessionContext = useCallback((context: Partial<PageContext>) => {
    const worksheetId = context.worksheet_id;
    if (!worksheetId) return;

    const otherRecentWorksheetIds = (userSessionContextRef.current.recentWorksheetIds ?? []).filter(
      (id) => id !== worksheetId,
    );
    userSessionContextRef.current = {
      ...userSessionContextRef.current,
      recentWorksheetIds: [worksheetId, ...otherRecentWorksheetIds].slice(0, MAX_RECENTLY_VISITED_WORKSHEETS),
    };
  }, []);

  const pageDetectedContextRef = useLatestRef(pageDetectedContext);
  const applyDetectedContext = useCallback(
    (context: PageContext) => {
      try {
        const pageContextChanged = JSON.stringify(pageDetectedContextRef.current) !== JSON.stringify(context);
        if (!pageContextChanged) {
          return;
        }
      } catch (_error) {}
      setBackgroundContext({});
      setPageDetectedContext(context);
      updateUserSessionContext(context);
    },
    [updateUserSessionContext, pageDetectedContextRef],
  );

  const buildUserSessionContextPayload = useCallback((): { user_context?: UserContextPayload } => {
    const { recentWorksheetIds } = userSessionContextRef.current;
    return recentWorksheetIds?.length ? { user_context: { recent_worksheets: recentWorksheetIds } } : {};
  }, []);

  // App action handler registry
  const appActionHandlersRef = useRef<Map<string, { handler: AppActionHandler; action: string }>>(new Map());

  const registerAppActionHandler = useCallback(
    ({ key, action, handler }: { key: string; action: string; handler: AppActionHandler }) => {
      appActionHandlersRef.current.set(key, { handler, action });
    },
    [],
  );

  const unregisterAppActionHandler = useCallback((key: string) => {
    appActionHandlersRef.current.delete(key);
  }, []);

  const bulkAppActionHandlersRef = useRef<Map<string, BulkAppActionHandler>>(new Map());

  const registerBulkAppActionHandler = useCallback(
    ({ key, handler }: { key: string; handler: BulkAppActionHandler }) => {
      bulkAppActionHandlersRef.current.set(key, handler);
    },
    [],
  );

  const unregisterBulkAppActionHandler = useCallback((key: string) => {
    bulkAppActionHandlersRef.current.delete(key);
  }, []);

  // App action registry (labels + optional inline buttons per agent message)
  const [messageAppActionsMap, setMessageAppActionsMap] = useState<Record<string, AppActionConfig[]> | undefined>(
    undefined,
  );

  // Insights provider registry. Deliberately first-match rather than the app-action registry's
  // fan-out to every match: a given screen only ever belongs to one product area's insights.
  //
  // State, not a ref: unlike the app-action registry (only ever read inside an async event
  // handler), `getMatchingInsightsProvider` is called directly from a component's render body.
  // A ref mutation triggers no re-render, so a provider that (re)registers after the consumer
  // already rendered — e.g. TQ re-registering a moment after a navigation, rather than in the same
  // tick — would never be noticed until something unrelated happened to re-render the consumer.
  const [insightsProviders, setInsightsProviders] = useState<Map<string, InsightsProvider>>(new Map());

  const registerInsightsProvider = useCallback((key: string, provider: InsightsProvider) => {
    setInsightsProviders((prev) => {
      const next = new Map(prev);
      next.set(key, provider);
      return next;
    });
  }, []);

  const unregisterInsightsProvider = useCallback((key: string) => {
    setInsightsProviders((prev) => {
      if (!prev.has(key)) return prev;
      const next = new Map(prev);
      next.delete(key);
      return next;
    });
  }, []);

  const getMatchingInsightsProvider = useCallback(
    (context: PageContext) => Array.from(insightsProviders.values()).find((provider) => provider.matches(context)),
    [insightsProviders],
  );

  // A single shared fetch, not one per consumer: both the header's count badge and the tab's own
  // list used to each call provider.fetchInsights() independently, doubling the request on every
  // drawer open. This is the one place it's fetched; both read the result from context.
  const [insights, setInsights] = useState<Insight[]>([]);
  const [insightsStatus, setInsightsStatus] = useState<'idle' | 'loading' | 'error'>('loading');

  const matchedInsightsProvider = useMemo(
    () => getMatchingInsightsProvider(pageDetectedContext),
    [getMatchingInsightsProvider, pageDetectedContext],
  );

  const refreshInsights = useCallback(async () => {
    if (!matchedInsightsProvider) {
      setInsights([]);
      setInsightsStatus('idle');
      return;
    }
    setInsightsStatus('loading');
    try {
      setInsights(await matchedInsightsProvider.fetchInsights());
      setInsightsStatus('idle');
    } catch (error) {
      console.error('[AIShell] Failed to fetch insights:', error);
      setInsightsStatus('error');
    }
  }, [matchedInsightsProvider]);

  useEffect(() => {
    // Gated on isOpen, not just the matched provider: this stays lazy like the tab's old
    // fetch-on-mount, rather than fetching in the background for every page that has a matching
    // provider registered regardless of whether the user ever opens the shell.
    if (!isOpen) return;
    refreshInsights();
  }, [isOpen, refreshInsights]);

  const registerMessageAppActions = useCallback((messageId: string, actions: AppActionConfig[]) => {
    setMessageAppActionsMap((prev) => ({ ...prev, [messageId]: actions }));
  }, []);

  const getMessageAppActions = useCallback(
    (messageId: string): AppActionConfig[] => {
      return messageAppActionsMap?.[messageId] ?? [];
    },
    [messageAppActionsMap],
  );

  // Panel-level buttons (rendered at top of chat)
  const [panelButtons, setPanelButtons] = useState<MessageButtonSpec[]>([]);

  const registerPanelButtons = useCallback((buttons: MessageButtonSpec[]) => {
    setPanelButtons(buttons);
  }, []);

  const getPanelButtons = useCallback((): MessageButtonSpec[] => {
    return panelButtons;
  }, [panelButtons]);

  const messagesFeedbackDataRef = useLatestRef(messagesFeedbackData);

  const handleFeedback = useCallback(
    async (messageId: string, feedback: number) => {
      if (!currentConversation) return;

      const currentFeedback = messagesFeedbackDataRef.current?.[messageId]?.feedback ?? null;
      const newFeedback = currentFeedback === feedback ? null : feedback;

      setMessagesFeedbackData((prev) => ({ ...prev, [messageId]: { feedback: newFeedback, response: undefined } }));

      try {
        const apiMessageId = messageId.replace(/:agent$/, '');
        await submitFeedback(currentConversation.id, apiMessageId, newFeedback);
      } catch (err) {
        console.error('[AIShell] Failed to submit feedback:', err);
        setMessagesFeedbackData((prev) => ({
          ...prev,
          [messageId]: { feedback: currentFeedback, response: undefined },
        }));
      }
    },
    [currentConversation, submitFeedback, messagesFeedbackDataRef],
  );

  const handleMessageRevealed = useCallback(() => {
    setMessageIdToReveal(undefined);
  }, []);

  const handleFeedbackResponse = useCallback(
    async (messageId: string, text: string) => {
      if (!currentConversation) return;

      const currentSentiment = messagesFeedbackDataRef.current?.[messageId]?.feedback;
      if (currentSentiment == null) return;

      setMessagesFeedbackData((prev) => ({ ...prev, [messageId]: { feedback: currentSentiment, response: text } }));

      try {
        const apiMessageId = messageId.replace(/:agent$/, '');
        await submitFeedback(currentConversation.id, apiMessageId, currentSentiment, text);
      } catch (err) {
        console.error('[AIShell] Failed to submit feedback response:', err);
        setMessagesFeedbackData((prev) => ({ ...prev, [messageId]: { feedback: currentSentiment, response: '' } }));
      }
    },
    [currentConversation, submitFeedback, messagesFeedbackDataRef],
  );

  // Context detection methods
  const updateContext = useCallback(
    (context: Record<string, string | undefined>) => {
      const next = { ...customContextRef.current };
      Object.entries(context).forEach(([key, value]) => {
        if (value) {
          next[key] = value;
        } else {
          delete next[key];
        }
      });
      customContextRef.current = next;
      updateUserSessionContext(next);
    },
    [updateUserSessionContext],
  );

  const refreshContext = useCallback(() => {
    if (contextDetection) {
      applyDetectedContext(contextDetection.detectContext());
    }
  }, [applyDetectedContext, contextDetection]);

  // Subscribe to context changes via adapter
  useEffect(() => {
    if (!contextDetection) {
      return undefined;
    }
    return contextDetection.subscribeToContextChanges((newContext) => {
      applyDetectedContext(newContext);
    });
  }, [contextDetection, applyDetectedContext]);

  useEffect(() => {
    if (!contextDetection) {
      return undefined;
    }
    const handler = () => setIsOpen(false);
    window.addEventListener(CIQ_SHELL_EVENTS.CLOSE_SHELL, handler);
    return () => window.removeEventListener(CIQ_SHELL_EVENTS.CLOSE_SHELL, handler);
  }, [contextDetection]);

  // Bridge for bundles that can't reach this provider directly (e.g. a TQ header button).
  // Gated on contextDetection like CLOSE_SHELL, so only the platform provider responds.
  // Toggles rather than always opening, since isOpen only exists here.
  const isOpenRef = useLatestRef(isOpen);
  useEffect(() => {
    if (!contextDetection) {
      return undefined;
    }
    const handleToggle = (event: Event) => {
      if (isOpenRef.current) {
        setIsOpen(false);
        return;
      }
      const tab = (event as CustomEvent<{ tab?: TabsEnum }>).detail?.tab;
      setIsOpen(true);
      if (tab !== undefined) {
        setTabIndex(tab);
      }
    };
    window.addEventListener(CIQ_SHELL_EVENTS.TOGGLE_SHELL_TAB, handleToggle);
    return () => window.removeEventListener(CIQ_SHELL_EVENTS.TOGGLE_SHELL_TAB, handleToggle);
  }, [contextDetection, isOpenRef]);

  // Bridge context updates from bundles that cannot reach this provider's React tree directly
  // (e.g. TQ, mounted as a separate bundle) into the same custom-context layer `updateContext` uses.
  useEffect(() => {
    const handleUpdate = (event: Event) => {
      updateContext((event as CustomEvent<Record<string, string | undefined>>).detail ?? {});
    };
    const handleClear = (event: Event) => {
      const keys = (event as CustomEvent<{ keys?: string[] }>).detail?.keys ?? Object.keys(customContextRef.current);
      updateContext(Object.fromEntries(keys.map((key) => [key, undefined])));
    };
    window.addEventListener(CIQ_SHELL_EVENTS.UPDATE_CONTEXT, handleUpdate);
    window.addEventListener(CIQ_SHELL_EVENTS.CLEAR_CONTEXT, handleClear);
    return () => {
      window.removeEventListener(CIQ_SHELL_EVENTS.UPDATE_CONTEXT, handleUpdate);
      window.removeEventListener(CIQ_SHELL_EVENTS.CLEAR_CONTEXT, handleClear);
    };
  }, [updateContext]);

  // Bridge insights-provider registration from bundles that cannot reach this provider's React
  // tree directly (e.g. TQ, mounted as a separate Module Federation remote with its own copy of
  // this package — `registerInsightsProvider` below would register into a different module
  // instance's registry, invisible to this one). A CustomEvent's `detail` is a live JS object, so
  // the provider's real function references survive the bundle boundary intact.
  useEffect(() => {
    const handleRegister = (event: Event) => {
      const detail = (event as CustomEvent<{ key: string; provider: InsightsProvider }>).detail;
      if (detail?.key && detail.provider) {
        registerInsightsProvider(detail.key, detail.provider);
      }
    };
    const handleUnregister = (event: Event) => {
      const key = (event as CustomEvent<{ key: string }>).detail?.key;
      if (key) {
        unregisterInsightsProvider(key);
      }
    };
    window.addEventListener(CIQ_SHELL_EVENTS.REGISTER_INSIGHTS_PROVIDER, handleRegister);
    window.addEventListener(CIQ_SHELL_EVENTS.UNREGISTER_INSIGHTS_PROVIDER, handleUnregister);
    return () => {
      window.removeEventListener(CIQ_SHELL_EVENTS.REGISTER_INSIGHTS_PROVIDER, handleRegister);
      window.removeEventListener(CIQ_SHELL_EVENTS.UNREGISTER_INSIGHTS_PROVIDER, handleUnregister);
    };
  }, [registerInsightsProvider, unregisterInsightsProvider]);

  const extractAndSetMessageSendError = useCallback(
    (error: unknown) => {
      if (error instanceof Error && error.message) {
        // Try to extract nested error message from strings like:
        // "AssistKit routing failed. Error code: 400 - {'error': {'message': '...'}}"
        const nestedMatch = error.message.match(/'message':\s*'([^']+)'/);
        setMessageSendError(nestedMatch ? nestedMatch[1] : error.message);
        return;
      }
      setMessageSendError(t('ai_shell.messages.error_sending_message'));
    },
    [t],
  );

  // Shared by every entry point that starts a fresh chat: drops the in-progress conversation and
  // its side channels so nothing from the previous agent/thread bleeds into the new one.
  const resetConversationState = useCallback(() => {
    setSelectedModel(DEFAULT_MODEL);
    setSelectedEffort(DEFAULT_EFFORT);
    setPlanMode(false);
    setMessageSendError(null);
    setCurrentConversation(null);
    setMessageAppActionsMap(undefined);
    setPanelButtons([]);
    setMessagesFeedbackData(undefined);
    setMessageIdToReveal(undefined);
  }, []);

  const openAgentShell: AIShellContextType['openAgentShell'] = useCallback(
    (agent, additionalContext) => {
      setSelectedAgent(agent);
      resetConversationState();
      setBackgroundContext(additionalContext ?? {});
      setIsOpen(true);
      setTabIndex(TabsEnum.CHAT);
      setChatView(ChatViewEnum.INTRO);
    },
    [setSelectedAgent, resetConversationState],
  );

  const openIntroFor: AIShellContextType['openIntroFor'] = useCallback(
    (agent) => {
      setSelectedAgent(agent);
      resetConversationState();
      setBackgroundContext({});
      setIsOpen(true);
      setTabIndex(TabsEnum.CHAT);
      setChatView(ChatViewEnum.INTRO);
    },
    [setSelectedAgent, resetConversationState],
  );

  const backToAgentBrowser: AIShellContextType['backToAgentBrowser'] = useCallback(() => {
    setSelectedAgent(null);
    setIsOpen(true);
    setTabIndex(TabsEnum.CHAT);
    setChatView(ChatViewEnum.BROWSER);
  }, [setSelectedAgent]);

  // Only fires while the user is actually on the Insights tab: navigating around the product on
  // any other tab must never force the drawer back to the agent picker.
  useEffect(() => {
    if (tabIndex === TabsEnum.INSIGHTS && !matchedInsightsProvider) {
      backToAgentBrowser();
    }
  }, [tabIndex, matchedInsightsProvider, backToAgentBrowser]);

  // Pure tab-index flips with no other side effects, so entering/leaving "All Chats" preserves
  // whatever conversation, agent, or chat view the user had in progress underneath.
  const openAllChats: AIShellContextType['openAllChats'] = useCallback(() => {
    setTabIndex(TabsEnum.HISTORY);
  }, []);

  const backToChat: AIShellContextType['backToChat'] = useCallback(() => {
    setTabIndex(TabsEnum.CHAT);
  }, []);

  const openNewChat: AIShellContextType['openNewChat'] = useCallback(() => {
    setSelectedAgent(null);
    resetConversationState();
    setIsOpen(true);
    setTabIndex(TabsEnum.CHAT);
    setChatView(ChatViewEnum.BROWSER);
  }, [setSelectedAgent, resetConversationState]);

  /**
   * Submit a turn via the async endpoint and poll for it, falling back to the synchronous
   * endpoint when the async one is not deployed. Shared by `send` and `resolveApproval` so
   * both paths behave identically.
   */
  const submitWithAsyncFallback = useCallback(
    async (
      conversationId: string,
      submitAsync: () => Promise<AsyncMessageSubmitResponse>,
      submitSync: () => Promise<PostMessageResponse>,
    ): Promise<PostMessageResponse> => {
      asyncPollControllerRef.current?.abort();
      const pollController = new AbortController();
      asyncPollControllerRef.current = pollController;

      try {
        setAgentStatusMessage(null);
        const submitResult = await submitAsync();

        const completedResult = await pollUntilDone<AsyncMessageStatusResponse>({
          pollFn: () => getMessageStatus(conversationId, submitResult.request_id),
          isDone: (r) => r.status !== 'pending',
          signal: pollController.signal,
          onIntermediate: (r) => {
            if (r.status === 'pending' && r.status_message) {
              setAgentStatusMessage(r.status_message);
            }
          },
        });

        if (completedResult.status === 'error') {
          // A polled failure arrives as a cached string with no HTTP response to read a status off,
          // so the task reports the code the sync endpoint would have returned. Re-attaching it here
          // is what lets callers treat a 409 as "resync", exactly as they do on the sync path.
          throw Object.assign(new Error(completedResult.error), {
            status: completedResult.status_code,
            [POLLED_ERROR_FLAG]: true,
          });
        }

        return (completedResult as { status: 'completed'; data: PostMessageResponse }).data;
      } catch (asyncErr) {
        if (asyncErr instanceof DOMException && asyncErr.name === 'AbortError') {
          throw asyncErr;
        }
        const errStatus = getErrorStatus(asyncErr);
        // Only a missing route falls back. A submit that reached the server and failed on its own
        // terms must not be replayed against the sync endpoint — for a decision that would be a
        // second attempt to resolve the same approval.
        if ((errStatus === 404 || errStatus === 405) && !isPolledError(asyncErr)) {
          return submitSync();
        }
        throw asyncErr;
      }
    },
    [getMessageStatus],
  );

  /**
   * Apply a completed agent turn to local state: append the message bubble, dispatch app
   * actions, then refresh the choices and pending-approval side channels.
   */
  const applyAgentTurn = useCallback((messageResponse: PostMessageResponse, userMessageId: string) => {
    const agentMessage = createAgentMessage(messageResponse);
    setAgentThinking(false); // Unset agent thinking before showing the latest message
    setAgentStatusMessage(null);
    setMessageIdToReveal(agentMessage.id);
    setCurrentConversation((prevConversation) => {
      if (!prevConversation) {
        return prevConversation; // unmounted
      }
      return {
        ...prevConversation,
        messages: [...prevConversation.messages, agentMessage],
      };
    });

    if (messageResponse.app_actions?.length) {
      const actionContext: AppActionContext = {
        userMessageId,
        agentMessageId: agentMessage.id,
      };
      const actionConfigs: AppActionConfig[] = messageResponse.app_actions.map((a) => ({ name: a.name }));
      setMessageAppActionsMap((prev) => ({ ...prev, [agentMessage.id]: actionConfigs }));
      messageResponse.app_actions.forEach(async (appAction) => {
        const handlerPromises: Array<Promise<unknown>> = [];
        for (const [, handlerData] of appActionHandlersRef.current.entries()) {
          const { action, handler } = handlerData;
          if (action === appAction.name) {
            handlerPromises.push(handler(appAction.action_data, actionContext));
          }
        }

        try {
          await Promise.all(handlerPromises);
        } catch (err) {
          console.error(`[AIShell] App action handler failed for "${appAction.name}":`, err);
        }
      });

      Array.from(bulkAppActionHandlersRef.current.values()).forEach((handler) => {
        handler(messageResponse.app_actions, actionContext);
      });
    }
  }, []);

  const send: AIShellContextType['send'] = useCallback(
    async (message, options) => {
      const resolvedContext = {
        ...defaultContext,
        ...backgroundContext,
        ...filterDetectedContext(pageDetectedContext),
        ...customContextRef.current,
        ...buildUserSessionContextPayload(),
        ...options?.context,
      };
      const attachmentMetas = options?.attachments ?? [];
      let conversationId: string | null = null;

      // Clear any previous error when starting a new send
      setMessageSendError(null);
      setMessageIdToReveal(undefined);

      if (currentConversation === null) {
        const agent = selectedAgentRef.current;
        if (agent === null) {
          throw Error('Trying to start a conversation without agent selected!');
        }
        const name = message.slice(0, 100);
        try {
          const conversation = await createConversation({
            name,
            agent_name: agent,
            context_metadata: resolvedContext,
          });
          conversationId = conversation.id;
        } catch (err) {
          extractAndSetMessageSendError(err);
          return;
        }
      } else {
        conversationId = currentConversation.id;
      }

      if (conversationId === null) {
        throw Error('Current conversation id is null! This should be impossible...');
      }
      // this reassign is to convince TS that the id is not null at this point
      const currentConversationId: string = conversationId;

      setAgentThinking(true);
      try {
        const userMessage = createUserMessage(message, undefined, attachmentMetas.length ? attachmentMetas : undefined);
        setCurrentConversation((prevConversation: CurrentConversation | null) => {
          if (prevConversation === null) {
            return {
              id: currentConversationId,
              messages: [userMessage],
            };
          }
          return {
            ...prevConversation,
            messages: [...prevConversation.messages, userMessage],
          };
        });

        const attachmentUuids = attachmentMetas.map((a) => a.uuid);
        const requestOptions: { model_override?: string; effort?: ReasoningEffort; mode?: AgentMode } = {};
        if (selectedModel.id !== DEFAULT_MODEL.id) {
          requestOptions.model_override = selectedModel.litellmName;
        }
        if (selectedEffort !== DEFAULT_EFFORT) {
          requestOptions.effort = selectedEffort;
        }
        if (planMode) {
          requestOptions.mode = PLAN_MODE;
        }
        const payload = {
          input_message: message,
          context: resolvedContext,
          ...(Object.keys(requestOptions).length > 0 ? { options: requestOptions } : {}),
          ...(attachmentUuids.length ? { attachments: attachmentUuids } : {}),
        };
        const messageResponse = await submitWithAsyncFallback(
          currentConversationId,
          () => sendMessageAsync(currentConversationId, payload),
          () => sendMessage(currentConversationId, payload),
        );

        applyAgentTurn(messageResponse, userMessage.id);
      } catch (err) {
        setAgentThinking(false);
        if (err instanceof DOMException && err.name === 'AbortError') {
          return;
        }
        extractAndSetMessageSendError(err);
      }
    },
    [
      defaultContext,
      backgroundContext,
      pageDetectedContext,
      buildUserSessionContextPayload,
      currentConversation,
      createConversation,
      extractAndSetMessageSendError,
      submitWithAsyncFallback,
      applyAgentTurn,
      sendMessageAsync,
      sendMessage,
      selectedModel,
      selectedEffort,
      planMode,
    ],
  );

  const openChatAndSend: AIShellContextType['openChatAndSend'] = useCallback(
    async (message, attachments) => {
      setIsOpen(true);
      setTabIndex(TabsEnum.CHAT);
      setChatView(ChatViewEnum.CHAT);
      await send(message, attachments?.length ? { attachments } : undefined);
    },
    [send],
  );

  /**
   * Loads a conversation's messages from the backend and replaces local state with them.
   * Unlike `openConversation`, this always reloads — used to resync the *current* conversation
   * after the backend reports it has moved on (e.g. an approval decision conflict).
   */
  const loadConversation = useCallback(
    async (id: CurrentConversation['id']) => {
      const conversationLoaded = await getSingleConversation(id);
      abortPendingConversationMessage();
      setSelectedModel(DEFAULT_MODEL);
      setSelectedEffort(DEFAULT_EFFORT);
      setPlanMode(false);
      setMessageSendError(null);
      setMessageAppActionsMap(undefined);
      setPanelButtons([]);
      setMessageIdToReveal(undefined);
      setSelectedAgent(conversationLoaded.agent_name as AgentNames);
      setBackgroundContext(conversationLoaded.context_metadata);
      setCurrentConversation({
        id,
        messages: conversationLoaded.messages.flatMap(parseAgentMessage),
      });

      const feedbackData: AIShellContextType['messagesFeedbackData'] = {};
      conversationLoaded.messages.forEach((msg) => {
        if (msg.feedback != null || msg.feedback_response) {
          feedbackData[`${msg.id}:agent`] = {
            feedback: msg.feedback ?? null,
            response: msg.feedback_response ?? undefined,
          };
        }
      });
      setMessagesFeedbackData(feedbackData);

      setIsOpen(true);
      setTabIndex(TabsEnum.CHAT);
      setChatView(ChatViewEnum.CHAT);
    },
    [getSingleConversation, setSelectedAgent, abortPendingConversationMessage],
  );

  const openConversation: AIShellContextType['openConversation'] = useCallback(
    async (id) => {
      if (id === currentConversation?.id) {
        return;
      }
      await loadConversation(id);
    },
    [currentConversation, loadConversation],
  );

  const hasAttemptedRestoreRef = useRef(false);
  const isRestoringConversationRef = useRef(!!initialConversationId);
  useEffect(() => {
    if (hasAttemptedRestoreRef.current || !initialConversationId) {
      return;
    }
    hasAttemptedRestoreRef.current = true;
    loadConversation(initialConversationId)
      .catch(() => {
        setChatView(ChatViewEnum.BROWSER);
        setSelectedAgent(null);
        setStoredConversationId(null);
      })
      .finally(() => {
        isRestoringConversationRef.current = false;
      });
  }, [initialConversationId, loadConversation, setSelectedAgent, setStoredConversationId]);

  useEffect(() => {
    if (isRestoringConversationRef.current) {
      return;
    }
    setStoredConversationId(
      isOpen && chatView === ChatViewEnum.CHAT && currentConversation?.id ? currentConversation.id : null,
    );
  }, [isOpen, chatView, currentConversation, setStoredConversationId]);

  const resolveApproval: AIShellContextType['resolveApproval'] = useCallback(
    async (approvalId, decision) => {
      const conversationId = currentConversation?.id;
      // Bind the decision to the exact action on screen. A control left over from an earlier
      // turn carries a stale approvalId and must not resolve whatever is pending now.
      if (!conversationId || agentThinking || pendingApproval?.approval_id !== approvalId) {
        return;
      }

      // Attribute any app_actions the approved tool emits to the request that prompted it.
      const lastUserMessageId =
        [...(currentConversation?.messages ?? [])].reverse().find((m) => m.type === 'user')?.id ?? approvalId;

      setMessageSendError(null);
      // pendingApproval is deliberately left in place: the card is hidden while agentThinking,
      // so if the request fails the control returns and the user can retry.
      setAgentThinking(true);
      try {
        const messageResponse = await submitWithAsyncFallback(
          conversationId,
          () => submitApprovalDecisionAsync(conversationId, approvalId, decision),
          () => submitApprovalDecision(conversationId, approvalId, decision),
        );

        if (
          decision === 'approved' &&
          pendingApproval?.tool_calls.some((call) => call.tool_name === EXIT_PLAN_MODE_TOOL_NAME)
        ) {
          setPlanMode(false);
        }
        applyAgentTurn(messageResponse, lastUserMessageId);
      } catch (err) {
        setAgentThinking(false);
        if (err instanceof DOMException && err.name === 'AbortError') {
          return;
        }
        // 409 means the backend already claimed this approval — another tab, or a retry that
        // landed twice. The action is genuinely resolved, so resync instead of showing an
        // error the user cannot act on.
        if (getErrorStatus(err) === 409) {
          await loadConversation(conversationId);
          return;
        }
        extractAndSetMessageSendError(err);
      }
    },
    [
      currentConversation,
      agentThinking,
      pendingApproval,
      submitWithAsyncFallback,
      applyAgentTurn,
      submitApprovalDecisionAsync,
      submitApprovalDecision,
      loadConversation,
      extractAndSetMessageSendError,
    ],
  );

  const appendAgentChatMessage: AIShellContextType['appendAgentChatMessage'] = useCallback((contents: string) => {
    const trimmed = contents.trim();
    if (!trimmed) {
      return;
    }
    setCurrentConversation((prev) => {
      if (prev === null) {
        return prev;
      }
      return {
        ...prev,
        messages: [...prev.messages, createCustomAgentMessage(trimmed)],
      };
    });
  }, []);

  const ensureConversation: AIShellContextType['ensureConversation'] = useCallback(async () => {
    if (currentConversation !== null) return currentConversation.id;

    const agent = selectedAgentRef.current;
    if (agent === null) throw new Error('No agent selected');

    const resolvedContext = {
      ...defaultContext,
      ...backgroundContext,
      ...filterDetectedContext(pageDetectedContext),
      ...customContextRef.current,
      ...buildUserSessionContextPayload(),
    };

    const conversation = await createConversation({
      name: '',
      agent_name: agent,
      context_metadata: resolvedContext,
    });
    setCurrentConversation({ id: conversation.id, messages: [] });
    return conversation.id;
  }, [
    currentConversation,
    defaultContext,
    backgroundContext,
    pageDetectedContext,
    buildUserSessionContextPayload,
    createConversation,
  ]);

  const shellContextValue = useMemo(
    () => ({
      isOpen,
      toggleShell,
      openShell,
      isExpanded,
      toggleExpanded,
      chatView,
      tabIndex,
      setTabIndex,
      agentThinking,
      agentStatusMessage,
      selectedAgent,
      setSelectedAgent,
      getConversations,
      messageSendError,
      openAgentShell,
      openIntroFor,
      backToAgentBrowser,
      openAllChats,
      backToChat,
      currentConversation,
      openNewChat,
      send,
      openChatAndSend,
      openConversation,
      abortPendingConversationMessage,
      appendAgentChatMessage,
      ensureConversation,
      createAttachment,
      // Context detection properties
      pageDetectedContext,
      updateContext,
      refreshContext,
      // App action handler registry
      registerAppActionHandler,
      unregisterAppActionHandler,
      registerBulkAppActionHandler,
      unregisterBulkAppActionHandler,
      // Insights provider registry
      registerInsightsProvider,
      unregisterInsightsProvider,
      getMatchingInsightsProvider,
      insights,
      insightsStatus,
      refreshInsights,
      registerMessageAppActions,
      getMessageAppActions,
      registerPanelButtons,
      getPanelButtons,
      choices,
      pendingApproval,
      resolveApproval,
      features,
      debugMode,
      toggleDebugMode,
      selectedModel,
      setSelectedModel,
      selectedEffort,
      setSelectedEffort,
      planMode,
      setPlanMode,
      feedbackEnabled: features.feedback === true,
      messagesFeedbackData,
      handleFeedback,
      handleFeedbackResponse,
      messageIdToReveal,
      handleMessageRevealed,
      activePromptOverride,
      deactivateActivePromptOverride,
    }),
    [
      isOpen,
      toggleShell,
      openShell,
      isExpanded,
      toggleExpanded,
      chatView,
      tabIndex,
      setTabIndex,
      agentThinking,
      agentStatusMessage,
      selectedAgent,
      setSelectedAgent,
      getConversations,
      messageSendError,
      openAgentShell,
      openIntroFor,
      backToAgentBrowser,
      openAllChats,
      backToChat,
      currentConversation,
      openNewChat,
      send,
      openChatAndSend,
      openConversation,
      abortPendingConversationMessage,
      appendAgentChatMessage,
      ensureConversation,
      createAttachment,
      // Context detection dependencies
      pageDetectedContext,
      updateContext,
      refreshContext,
      // App action handler registry
      registerAppActionHandler,
      unregisterAppActionHandler,
      registerBulkAppActionHandler,
      unregisterBulkAppActionHandler,
      // Insights provider registry
      registerInsightsProvider,
      unregisterInsightsProvider,
      getMatchingInsightsProvider,
      insights,
      insightsStatus,
      refreshInsights,
      registerMessageAppActions,
      getMessageAppActions,
      registerPanelButtons,
      getPanelButtons,
      choices,
      pendingApproval,
      resolveApproval,
      features,
      debugMode,
      toggleDebugMode,
      selectedModel,
      setSelectedModel,
      selectedEffort,
      setSelectedEffort,
      planMode,
      messagesFeedbackData,
      handleFeedback,
      handleFeedbackResponse,
      messageIdToReveal,
      handleMessageRevealed,
      activePromptOverride,
      deactivateActivePromptOverride,
    ],
  );
  return (
    <I18nextProvider i18n={assistantI18n}>
      <AIShellContext.Provider value={shellContextValue}>{children}</AIShellContext.Provider>
    </I18nextProvider>
  );
};
